import React, { useMemo, PropsWithChildren, createContext } from 'react';
import { observer } from 'mobx-react';
import { useGlobalMX } from '~/providers/mx-global-context/useGlobalMX';
import {
    HBStatsigProvider,
    HBStatsigProviderProps
} from '~/common/Statsig/HBStatsigProvider';
import useUiPersistedState from '~/common/useUiPersistedState';
import { useWarnAboutNestedMXStatsigProvider } from '~/common/Statsig/Authenticated/MX/useWarnAboutNestedMXStatsigProvider';
import { useIsWrappedWithMXStatsigProvider } from '~/common/Statsig/Authenticated/MX/useIsWrappedWithMXStatsigProvider';
import { isReactFirst } from '~/utils/environment.utils';

export const HBMXStatsigProviderWrapper = observer(
    ({ children }: PropsWithChildren<{}>) => {
        return <HBMXStatsigProvider>{children}</HBMXStatsigProvider>;
    }
);

function useMXStatsigProviderProps() {
    const [{ value: wasInvoiceSent }] = useUiPersistedState('wasInvoiceSent', {
        value: false
    });

    const [isCompletedBrandImport] = useUiPersistedState(
        'isCompletedBrandImport',
        { value: false }
    );

    return useMemo(
        () => ({
            was_invoice_sent: wasInvoiceSent,
            is_completed_brand_import: isCompletedBrandImport.value
        }),
        [wasInvoiceSent, isCompletedBrandImport.value]
    );
}

// Create a context to track nesting (if a StatsigProvider already exists as a parent)
export const HBMXStatsigProviderExistsContext = createContext<boolean>(false);

const HBMXStatsigProviderContent = ({
    children,
    name,
    // in react first every provider is nested, so we silence the warning
    silenceIsNestedWarning = isReactFirst() ? true : false
}: PropsWithChildren<HBStatsigProviderProps>) => {
    const {
        configStore,
        networkStore,
        userStore: { currUser },
        companyStore: { currCompany }
    } = useGlobalMX();

    const additionalCustomAttributes = useMXStatsigProviderProps();
    const isWrappedWithMXStatsigProvider = useIsWrappedWithMXStatsigProvider();

    useWarnAboutNestedMXStatsigProvider({
        silenceIsNestedWarning,
        name
    });

    // Use the context to determine if this provider is nested (already has a provider as parent)
    if (isWrappedWithMXStatsigProvider) {
        return <>{children}</>;
    }

    return (
        <HBStatsigProvider
            configStore={configStore}
            networkStore={networkStore}
            currUser={currUser}
            currCompany={currCompany}
            overrideUserCustomAttributes={additionalCustomAttributes}
        >
            {/*tell all children that they're wrapped with a provider*/}
            <HBMXStatsigProviderExistsContext.Provider value={true}>
                {children}
            </HBMXStatsigProviderExistsContext.Provider>
        </HBStatsigProvider>
    );
};

export const HBMXStatsigProvider = observer(HBMXStatsigProviderContent);
